<template>
  <div class="panel-distribuidor-curso product-detail-wrapper w-100 tw-bg-gray-50 dark:tw-bg-gray-900 tw-min-h-screen">
    
    <!-- LOADING STATE -->
    <div v-if="loading" class="d-flex justify-content-center align-items-center" style="height: 100vh;">
      <div class="spinner-border text-primary" style="width: 4rem; height: 4rem;" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <!-- ERROR STATE -->
    <div v-else-if="error || !course" class="container py-5 text-center">
      <div class="card border-0 shadow-sm p-5 text-center my-4 mx-auto" style="border-radius: 20px; max-width: 600px;">
        <div class="mb-4">
          <i class="fas fa-exclamation-circle" style="color: #EF4444; font-size: 72px;"></i>
        </div>
        <h3 class="font-weight-bold mb-2 text-dark">Curso no encontrado</h3>
        <p class="text-muted mb-4 mx-auto">
          Error al cargar la información del curso. Por favor, intenta de nuevo más tarde.
        </p>
        <button @click="$router.push('/panel-distribuidor')" class="btn btn-primary tw-rounded-full tw-px-6">Volver al panel</button>
      </div>
    </div>

    <!-- MAIN VIEW -->
    <div v-else class="tw-pb-20">
      
      <!-- Fondo del header (Banner similar a Udemy) -->
      <div class="marketplace-header-bg tw-mb-12 md:tw-mb-16 tw--mx-4 md:tw--mx-8 tw-mt-4 md:tw-mt-8 tw-px-4 md:tw-px-8" style="min-height: 380px;">
        <div class="container-fluid">
          <div class="row justify-content-center">
            <div class="col-12 col-xl-11">
              <div class="row">
                <div class="col-12 col-lg-8">
                  
                  <!-- Breadcrumb Navigation -->
                  <nav aria-label="breadcrumb" class="mb-4">
                    <ol class="breadcrumb custom-breadcrumb bg-transparent p-0 mb-0">
                      <li class="breadcrumb-item">
                        <router-link to="/panel-distribuidor" class="udemy-text-link tw-text-blue-600 dark:tw-text-blue-400 hover:tw-text-blue-800 dark:hover:tw-text-blue-300 tw-font-bold tw-transition-colors">
                          Cursos
                        </router-link>
                      </li>
                      <li class="breadcrumb-item active text-truncate max-w-200 tw-text-gray-600 dark:tw-text-gray-300" aria-current="page">
                        <span class="tw-text-gray-500 tw-mx-2">></span>{{ course.title }}
                      </li>
                    </ol>
                  </nav>
                  
                  <h1 class="product-main-title mb-3 tw-text-3xl md:tw-text-4xl tw-font-bold tw-text-gray-900 dark:tw-text-white tw-leading-tight">
                    {{ course.title }}
                  </h1>
                  
                  <p class="product-description-subtitle mb-4 tw-text-lg tw-text-gray-700 dark:tw-text-gray-200" v-if="course.description">
                    {{ course.description.length > 200 ? course.description.substring(0, 200) + '...' : course.description }}
                  </p>

                  <div class="d-flex flex-wrap align-items-center gap-3 mb-3">
                    <span class="bestseller-badge udemy-bg-badge udemy-text-badge tw-font-bold tw-px-2 tw-py-1 tw-text-xs">MARKETPLACE</span>
                    
                    <div class="d-flex align-items-center rating-reviews-row">
                      <span class="rating-score-text font-weight-bold udemy-text-rating tw-mr-1">5.0</span>
                      <div class="tw-flex tw-items-center tw-mr-2">
                        <svg v-for="i in 5" :key="i" class="tw-w-4 tw-h-4 tw-text-[#F59E0B]" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
                      </div>
                    </div>
                  </div>
                  
                  <div class="d-flex align-items-center gap-1 mb-2 tw-text-sm">
                    <span class="tw-text-gray-600 dark:tw-text-gray-200">Creado por</span>
                    <span class="udemy-text-link tw-text-blue-600 dark:tw-text-blue-400 tw-underline">{{ course.creator || 'Instructor Oficial' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- MAIN CONTENT AREA -->
      <div class="container-fluid py-5 tw-mt-[-100px]">
        <div class="row justify-content-center">
          <div class="col-12 col-xl-11">
            <div class="row position-relative">
              
              <!-- Columna Izquierda: Contenido Principal -->
              <div class="col-12 col-lg-8 pr-lg-5">
                
                <!-- Lo que aprenderás -->
                <div v-if="course.will_learn" class="tw-border tw-border-gray-300 dark:tw-border-gray-700 tw-p-6 tw-mb-8 tw-bg-white dark:tw-bg-gray-800 tw-rounded-lg">
                  <h2 class="tw-text-2xl tw-font-bold tw-mb-4 tw-text-gray-900 dark:tw-text-white">Lo que aprenderás</h2>
                  <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                    <div class="d-flex align-items-start gap-2" v-for="(item, index) in formatList(course.will_learn)" :key="index">
                      <svg class="tw-w-5 tw-h-5 tw-mt-0.5 tw-text-gray-900 dark:tw-text-gray-300 tw-flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>
                      <span class="tw-text-sm tw-text-gray-700 dark:tw-text-gray-300">{{ item }}</span>
                    </div>
                  </div>
                </div>

                <!-- Requisitos -->
                <div v-if="course.prev_knowledge && course.prev_knowledge !== 'Ninguno'" class="tw-mb-8">
                  <h2 class="tw-text-2xl tw-font-bold tw-mb-4 tw-text-gray-900 dark:tw-text-white">Requisitos</h2>
                  <ul class="tw-list-disc tw-pl-5 tw-mb-6 tw-text-sm tw-text-gray-700 dark:tw-text-gray-300">
                    <li v-for="(item, index) in formatList(course.prev_knowledge)" :key="'prev-'+index" class="tw-mb-1">{{ item }}</li>
                  </ul>
                </div>

                <!-- Descripción -->
                <div class="tw-mb-10">
                  <h2 class="tw-text-2xl tw-font-bold tw-mb-4 tw-text-gray-900 dark:tw-text-white">Descripción</h2>
                  <div class="product-description-text tw-text-sm tw-text-gray-700 dark:tw-text-gray-300 tw-whitespace-pre-line tw-leading-relaxed">
                    {{ course.description }}
                    
                    <template v-if="course.course_about">
                      <br><br>
                      <strong>Acerca de este curso:</strong>
                      <br>{{ course.course_about }}
                    </template>
                    
                    <template v-if="course.course_for">
                      <br><br>
                      <strong>¿A quién está dirigido?</strong>
                      <br>{{ course.course_for }}
                    </template>
                  </div>
                </div>

                <!-- Materiales y Recursos Promocionales -->
                <div v-if="(course.marketing_materials && course.marketing_materials.length > 0) || (course.masterclasses && course.masterclasses.length > 0) || (course.ebooks && course.ebooks.length > 0) || (course.minicourses && course.minicourses.length > 0)" class="tw-mb-10 tw-border tw-border-gray-200 dark:tw-border-gray-700 tw-rounded-lg tw-p-6 tw-bg-white dark:tw-bg-gray-800">
                  <h2 class="tw-text-2xl tw-font-bold tw-mb-4 tw-text-gray-900 dark:tw-text-white">Material Promocional</h2>
                  
                  <!-- Marketing Materials -->
                  <div v-if="course.marketing_materials && course.marketing_materials.length > 0" class="tw-mb-6">
                    <div v-if="descripciones.length > 0">
                      <h3 class="tw-text-lg tw-font-bold tw-mb-4 tw-text-gray-800 dark:tw-text-gray-200">Textos Persuasivos (Copy)</h3>
                      <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-mb-6">
                        <div v-for="item in descripciones" :key="'desc-'+item.id" class="tw-bg-gray-50 dark:tw-bg-gray-700 tw-p-4 tw-rounded-lg tw-border tw-border-gray-200 dark:tw-border-gray-600 tw-flex tw-flex-col">
                          <h4 class="tw-font-bold tw-text-sm tw-mb-2 tw-text-gray-900 dark:tw-text-white"><i class="fas fa-file-alt tw-mr-1 tw-text-[var(--primary-color,#18d600)]"></i> {{ item.title || 'Texto Promocional' }}</h4>
                          <p class="tw-text-sm tw-text-gray-700 dark:tw-text-gray-300 tw-whitespace-pre-line tw-flex-grow tw-mb-4">{{ item.content }}</p>
                          <button @click="copyText(item.content)" class="btn btn-outline-primary btn-sm tw-w-full" style="border-color: var(--primary-color, #18d600); color: var(--primary-color, #18d600);">
                            <i class="far fa-copy tw-mr-1"></i> Copiar Texto
                          </button>
                        </div>
                      </div>
                    </div>

                    <div v-if="imagenes.length > 0">
                      <h3 class="tw-text-lg tw-font-bold tw-mb-4 tw-text-gray-800 dark:tw-text-gray-200">Flyers y Banners</h3>
                      <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 md:tw-grid-cols-3 tw-gap-4 tw-mb-6">
                        <div v-for="item in imagenes" :key="'img-'+item.id" class="tw-bg-gray-50 dark:tw-bg-gray-700 tw-rounded-lg tw-border tw-border-gray-200 dark:tw-border-gray-600 tw-overflow-hidden tw-flex tw-flex-col">
                          <div class="tw-aspect-[4/5] tw-w-full tw-bg-gray-200 dark:tw-bg-gray-800">
                            <img :src="getMediaUrl(item.file_url || item.file_path)" class="tw-w-full tw-h-full tw-object-cover" />
                          </div>
                          <div class="tw-p-3 tw-flex tw-gap-2">
                            <a :href="getMediaUrl(item.file_url || item.file_path)" target="_blank" class="btn btn-sm tw-flex-1 tw-text-xs tw-text-white" style="background-color: var(--primary-color, #18d600);"><i class="fas fa-download tw-mr-1"></i> Descargar</a>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div v-if="videos.length > 0">
                      <h3 class="tw-text-lg tw-font-bold tw-mb-4 tw-text-gray-800 dark:tw-text-gray-200">Videos Promocionales</h3>
                      <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-mb-6">
                        <div v-for="item in videos" :key="'vid-'+item.id" class="tw-bg-gray-50 dark:tw-bg-gray-700 tw-rounded-lg tw-border tw-border-gray-200 dark:tw-border-gray-600 tw-overflow-hidden tw-flex tw-flex-col">
                          <div class="tw-aspect-video tw-w-full tw-bg-black">
                            <video :src="getMediaUrl(item.file_url || item.file_path)" controls preload="metadata" class="tw-w-full tw-h-full"></video>
                          </div>
                          <div class="tw-p-3 tw-flex tw-gap-2">
                            <a :href="getMediaUrl(item.file_url || item.file_path)" target="_blank" class="btn btn-sm tw-flex-1 tw-text-xs tw-text-white" style="background-color: var(--primary-color, #18d600);"><i class="fas fa-download tw-mr-1"></i> Descargar</a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Masterclasses -->
                  <div v-if="course.masterclasses && course.masterclasses.length > 0" class="tw-mb-6">
                    <h3 class="tw-text-lg tw-font-bold tw-mb-3 tw-text-gray-800 dark:tw-text-gray-200">Masterclasses</h3>
                    <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                      <div v-for="mc in course.masterclasses" :key="'mc-'+mc.id" class="tw-bg-gray-50 dark:tw-bg-gray-700 tw-rounded-lg tw-border tw-border-gray-200 dark:tw-border-gray-600 tw-overflow-hidden tw-flex tw-flex-col">
                        <div class="tw-aspect-video tw-w-full tw-bg-gray-200 dark:tw-bg-gray-800 tw-relative">
                          <img v-if="mc.image || mc.cover_image" :src="getMediaUrl(mc.image || mc.cover_image)" class="tw-w-full tw-h-full tw-object-cover" />
                          <div v-else class="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center"><i class="fas fa-chalkboard-teacher tw-text-4xl tw-text-gray-400"></i></div>
                        </div>
                        <div class="tw-p-4 tw-flex-grow">
                          <h5 class="tw-font-bold tw-text-sm tw-mb-2 tw-text-gray-900 dark:tw-text-white">{{ mc.title }}</h5>
                          <p class="tw-text-xs tw-text-gray-600 dark:tw-text-gray-300 tw-mb-3"><i class="far fa-calendar-alt tw-mr-1"></i> Fecha: {{ mc.date || 'Por definir' }} {{ mc.hour || '' }}</p>
                          <a :href="mc.file_path || mc.video_url || mc.link" target="_blank" class="btn btn-outline-primary btn-sm tw-w-full" style="border-color: var(--primary-color, #18d600); color: var(--primary-color, #18d600);"><i class="fas fa-link tw-mr-1"></i> Ver Masterclass</a>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- E-books -->
                  <div v-if="course.ebooks && course.ebooks.length > 0" class="tw-mb-6">
                    <h3 class="tw-text-lg tw-font-bold tw-mb-3 tw-text-gray-800 dark:tw-text-gray-200">E-books</h3>
                    <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                      <a 
                        v-for="ebook in course.ebooks" 
                        :key="'eb-'+ebook.id"
                        :href="ebook.file_path"
                        target="_blank"
                        class="d-flex align-items-center tw-bg-gray-50 dark:tw-bg-gray-700 tw-p-3 tw-rounded-lg tw-border tw-border-gray-200 dark:tw-border-gray-600 hover:tw-border-[var(--primary-color,#18d600)] tw-transition-colors tw-no-underline tw-text-gray-900 dark:tw-text-white tw-shadow-sm"
                      >
                        <div class="tw-w-10 tw-h-10 tw-rounded-full tw-bg-green-100 dark:tw-bg-green-900 tw-flex tw-items-center tw-justify-center tw-mr-3 tw-flex-shrink-0">
                          <i class="fa-solid fa-book tw-text-[var(--primary-color,#18d600)]"></i>
                        </div>
                        <span class="tw-text-sm tw-font-semibold tw-flex-grow">
                          {{ ebook.title || 'E-book' }}
                        </span>
                        <i class="fa-solid fa-download tw-text-gray-400 hover:tw-text-[var(--primary-color,#18d600)] tw-transition-colors"></i>
                      </a>
                    </div>
                  </div>
                  
                  <!-- Mini Cursos -->
                  <div v-if="course.minicourses && course.minicourses.length > 0">
                    <h3 class="tw-text-lg tw-font-bold tw-mb-3 tw-text-gray-800 dark:tw-text-gray-200">Mini Cursos</h3>
                    <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                      <a 
                        v-for="mc in course.minicourses" 
                        :key="'min-'+mc.id"
                        :href="mc.file_path || mc.video_url"
                        target="_blank"
                        class="d-flex align-items-center tw-bg-gray-50 dark:tw-bg-gray-700 tw-p-3 tw-rounded-lg tw-border tw-border-gray-200 dark:tw-border-gray-600 hover:tw-border-[var(--primary-color,#18d600)] tw-transition-colors tw-no-underline tw-text-gray-900 dark:tw-text-white tw-shadow-sm"
                      >
                        <div class="tw-w-10 tw-h-10 tw-rounded-full tw-bg-green-100 dark:tw-bg-green-900 tw-flex tw-items-center tw-justify-center tw-mr-3 tw-flex-shrink-0">
                          <i class="fa-solid fa-film tw-text-[var(--primary-color,#18d600)]"></i>
                        </div>
                        <span class="tw-text-sm tw-font-semibold tw-flex-grow">
                          {{ mc.title || 'Mini Curso' }}
                        </span>
                        <i class="fa-solid fa-download tw-text-gray-400 hover:tw-text-[var(--primary-color,#18d600)] tw-transition-colors"></i>
                      </a>
                    </div>
                  </div>

                </div>
                <div v-else class="tw-mb-10 tw-border tw-border-gray-200 dark:tw-border-gray-700 tw-rounded-lg tw-p-6 tw-bg-white dark:tw-bg-gray-800">
                  <h2 class="tw-text-2xl tw-font-bold tw-mb-4 tw-text-gray-900 dark:tw-text-white">Material Promocional</h2>
                  <div class="tw-text-sm tw-text-gray-500">
                    No hay materiales disponibles aún para este curso.
                  </div>
                </div>

              </div>

              <!-- Columna Derecha: Tarjeta Flotante (Udemy Sidebar Card) -->
              <div class="col-12 col-lg-4 tw-relative tw-z-10">
                <div class="udemy-sticky-card udemy-desktop-fixed tw-bg-white dark:tw-bg-gray-800 tw-shadow-2xl tw-border tw-border-gray-200 dark:tw-border-gray-700 tw-mb-4 lg:tw-mb-0">
                  
                  <!-- Contenedor Multimedia Principal -->
                  <div class="product-media-container tw-w-full tw-bg-black tw-relative">
                    <video v-if="hasPromoVideo" :src="getPromoVideoUrl(course)" controls class="promo-thumbnail" style="border-radius: 0 !important; width:100%; aspect-ratio: 16/9; object-fit: cover;"></video>
                    <img v-else :src="getCourseImage(course)" class="promo-thumbnail" :alt="course.title" style="border-radius: 0 !important;" />
                  </div>

                  <div class="tw-px-4 tw-pt-3 tw-pb-4">
                    <!-- Tarjeta de Precio -->
                    <div class="tw-flex tw-items-end tw-gap-3 tw-mb-2">
                      <div class="tw-text-3xl tw-font-extrabold tw-text-gray-900 dark:tw-text-white tw-flex tw-items-baseline tw-gap-1">
                        <span>${{ getCoursePrice(course) }}</span> <span class="tw-text-sm tw-font-semibold tw-text-gray-500 tw-ml-1">USD</span>
                      </div>
                    </div>

                    <div class="tw-px-0">
                      <!-- Botones de Acción -->
                      <div class="action-buttons-wrapper d-flex flex-column gap-2 mb-2">
                        <button @click="copyShareLink" class="btn w-100 py-3 d-flex justify-content-center align-items-center" style="background-color: #18d600; color: white; font-weight: 700; font-size: 1.1rem; border-radius: 8px; border: none; box-shadow: 0 4px 6px rgba(24, 214, 0, 0.2);">
                          <i class="fa-solid fa-share-nodes mr-2" style="margin-right: 8px;"></i> Copiar Enlace de Referido
                        </button>
                      </div>

                      <!-- Beneficios del Curso -->
                      <div class="trust-features-list border-top-subtle pt-3 mb-2 mt-4">
                        <div class="trust-feature-item d-flex align-items-start mb-2">
                          <svg width="16" height="16" class="tw-mr-3 mt-1 text-emerald-500" style="color: var(--primary-color, #18d600); flex-shrink: 0;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                          </svg>
                          <div>
                            <span class="trust-title tw-text-gray-900 dark:tw-text-white tw-text-sm tw-font-bold">Acceso inmediato</span>
                            <p class="trust-desc mb-0 tw-text-gray-600 dark:tw-text-gray-300 tw-text-xs">Acceso de por vida a lecciones.</p>
                          </div>
                        </div>

                        <div class="trust-feature-item d-flex align-items-start mb-2">
                          <svg width="16" height="16" class="tw-mr-3 mt-1 text-emerald-500" style="color: var(--primary-color, #18d600); flex-shrink: 0;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"></path>
                          </svg>
                          <div>
                            <span class="trust-title tw-text-gray-900 dark:tw-text-white tw-text-sm tw-font-bold">Certificado Oficial</span>
                            <p class="trust-desc mb-0 tw-text-gray-600 dark:tw-text-gray-300 tw-text-xs">Certificado al completar el curso.</p>
                          </div>
                        </div>

                        <div class="trust-feature-item d-flex align-items-start">
                          <svg width="16" height="16" class="tw-mr-3 mt-1 text-emerald-500" style="color: var(--primary-color, #18d600); flex-shrink: 0;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                          </svg>
                          <div>
                            <span class="trust-title tw-text-gray-900 dark:tw-text-white tw-text-sm tw-font-bold">Pago Seguro</span>
                            <p class="trust-desc mb-0 tw-text-gray-600 dark:tw-text-gray-300 tw-text-xs">Transacciones con cifrado SSL.</p>
                          </div>
                        </div>
                      </div>

                      <!-- Tarjeta de Instructor -->
                      <div class="productor-card-box tw-mt-6 tw-pt-5 tw-pb-2 tw-border-t tw-border-gray-200 dark:tw-border-gray-700">
                        <div class="productor-card-content tw-flex tw-items-center tw-gap-3.5">
                          <div class="productor-avatar tw-w-11 tw-h-11 tw-flex-shrink-0 tw-rounded-full tw-overflow-hidden tw-mr-3 tw-bg-gray-200 dark:tw-bg-gray-600 d-flex justify-content-center align-items-center">
                            <i class="fa-solid fa-user tw-text-gray-400 tw-text-xl" style="margin-top: 10px;"></i>
                          </div>
                          <div class="productor-info">
                            <div class="tw-font-bold tw-text-sm tw-text-gray-900 dark:tw-text-white tw-mb-0.5">
                              {{ course.creator || 'Instructor Oficial' }}
                            </div>
                            <div class="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
                              Instructor
                            </div>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
      
    </div>

    <!-- Reusable Custom Alert -->
    <CustomAlert 
      v-if="alert.show"
      :type="alert.type"
      :message="alert.message"
      :defaultTitle="alert.title"
      class="tw-fixed tw-bottom-4 tw-right-4 tw-z-50"
    />
    
    <ShareModal 
      v-if="showShareModal" 
      :show="showShareModal" 
      :share-link="shareLink" 
      :course-title="course?.title" 
      @close="showShareModal = false" 
    />
  </div>
</template>

<script>
import { authGet } from "@/helpers/authStorage";
import CustomAlert from "@/components/CustomAlert.vue";
import ShareModal from "@/components/Modals/ShareModal.vue";

export default {
  name: "PanelDistribuidorCurso",
  components: {
    CustomAlert,
    ShareModal
  },
  data() {
    return {
      course: null,
      loading: true,
      error: false,
      showShareModal: false,
      shareLink: '',
      alert: {
        show: false,
        type: 'success',
        title: '',
        message: ''
      }
    };
  },
  computed: {
    descripciones() {
      return (this.course?.marketing_materials || []).filter(m => m.type === 'description' || m.type === 'text');
    },
    imagenes() {
      return (this.course?.marketing_materials || []).filter(m => m.type === 'image' || m.type === 'banner' || m.type === 'flyer');
    },
    videos() {
      return (this.course?.marketing_materials || []).filter(m => m.type === 'video');
    },
    hasPromoVideo() {
      if (!this.course) return false;
      const url = this.course.path_url;
      if (!url) return false;
      return url.endsWith('.mp4') || url.endsWith('.webm') || url.endsWith('.ogg');
    }
  },
  async created() {
    const courseId = this.$route.params.id;
    if (!courseId) {
      this.error = true;
      this.loading = false;
      return;
    }
    await this.fetchCourse(courseId);
  },
  methods: {
    async fetchCourse(id) {
      try {
        this.loading = true;
        const response = await this.axios.get("marketing/marketplace/courses?per_page=1000");
        if (response.data && response.data.data) {
          const fetchedCourses = response.data.data.data || response.data.data;
          this.course = fetchedCourses.find(c => parseInt(c.id) === parseInt(id));
          if (!this.course) {
            this.error = true;
          } else {
            // Obtener materiales promocionales y masterclasses desde el endpoint de resources
            try {
              const res = await this.axios.get(`marketing/marketplace/courses/${id}/resources`);
              if (res.data && res.data.data) {
                // Forzar actualización de reactividad
                this.course = {
                  ...this.course,
                  marketing_materials: res.data.data.promotional_materials || [],
                  masterclasses: res.data.data.masterclasses || [],
                  ebooks: res.data.data.ebooks || [],
                  minicourses: res.data.data.minicourses || []
                };
              }
            } catch (resourceErr) {
              console.error("Error fetching course resources:", resourceErr);
            }
          }
        }
      } catch (err) {
        console.error("Error fetching course details:", err);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    formatList(text) {
      if (!text) return [];
      return text.split(/\r?\n|;/).map(s => s.trim()).filter(Boolean);
    },
    getMediaUrl(path) {
      if (!path) return '';
      if (path.includes('api.promolider.email')) {
        path = path.replace(/https?:\/\/api\.promolider\.email/g, '');
      }
      if (path.startsWith('http://') || path.startsWith('https://')) return path;
      const cleanPath = path.startsWith('/') ? path : '/' + path;
      return 'https://promolider-storage-user.s3-accelerate.amazonaws.com' + cleanPath;
    },
    getCourseImage(course) {
      const url = course.url_portada || course.image || course.url_front_page;
      if (!url) return 'https://via.placeholder.com/640x360?text=Curso';
      if (url.startsWith('http')) return url.replace('s3.sa-east-1', 's3-accelerate');
      return "https://promolider-storage-user.s3-accelerate.amazonaws.com/" + url;
    },
    getPromoVideoUrl(course) {
      const url = course.path_url;
      if (!url) return '';
      if (url.startsWith('http')) return url.replace('s3.sa-east-1', 's3-accelerate');
      return "https://promolider-storage-user.s3-accelerate.amazonaws.com/" + url;
    },
    getCoursePrice(course) {
      if (course.price) return parseFloat(course.price).toFixed(2);
      if (course.price_base) return parseFloat(course.price_base).toFixed(2);
      return '0.00';
    },
    copyShareLink() {
      const idUser = authGet("id_user");
      if (!idUser) {
        this.showAlert('error', 'Error', 'No se pudo obtener tu usuario de referido.');
        return;
      }
      const baseUrl = window.location.origin; 
      this.shareLink = `${baseUrl}/landing/course/${this.course.id}?ref=${idUser}`;
      this.showShareModal = true;
    },
    copyText(text) {
      if (!text) return;
      navigator.clipboard.writeText(text).then(() => {
        this.showAlert('success', '¡Copiado!', 'El texto promocional se ha copiado al portapapeles.');
      }).catch(err => {
        console.error('Error al copiar el texto: ', err);
        this.showAlert('error', 'Error', 'No se pudo copiar el texto.');
      });
    },
    showAlert(type, title, message) {
      this.alert.type = type;
      this.alert.title = title;
      this.alert.message = message;
      this.alert.show = true;
      if (this.alertTimer) clearTimeout(this.alertTimer);
      this.alertTimer = setTimeout(() => { this.alert.show = false; }, 3000);
    }
  }
};
</script>

<style scoped>
@media (min-width: 992px) {
  .udemy-desktop-fixed {
    position: fixed !important;
    top: 140px !important;
    width: 100% !important;
    max-width: 300px !important;
    z-index: 50 !important;
  }
}

/* Custom Brand Green Styles */
.btn-share {
  background-color: #111827; /* gray-900 */
  color: white;
  font-weight: 600;
  transition: background-color 0.2s;
  display: flex;
  justify-content: center;
  align-items: center;
}
.btn-share:hover {
  background-color: var(--primary-color, #18d600);
}

/* Banners del Marketplace (Claro/Oscuro) */
.marketplace-header-bg {
  background-image: url('/banner-market-claro.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  transition: background-image 0.3s ease;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  padding-top: 100px !important;
  padding-bottom: 120px !important;
}

html.dark-mode body .marketplace-header-bg, html.dark body .marketplace-header-bg {
  background-image: url('/banner-market-oscuro_1.png');
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
</style>
